import { z } from 'zod';
import { Locale, Slug, TimeZone, Uuid } from './common.js';
import { TenantRestriction } from './core-p4.js';
import { Branding } from './settings.js';
import { SiteScope } from './users.js';
/** One portal menu entry, contributed by a module manifest (`nav`). */
export const NavEntry = z.object({
    /** Unique, e.g. 'core.users'. */
    key: z.string(),
    /** Which menu: tenant admin area, platform area, or the user's own account menu. */
    area: z.enum(['tenant', 'platform', 'account', 'building']),
    /** Section heading i18n key, e.g. 'nav.section.admin'. */
    sectionKey: z.string(),
    labelKey: z.string(),
    /** Portal path, e.g. '/admin/users'. */
    path: z.string(),
    /** lucide icon name, e.g. 'users'. */
    icon: z.string(),
    /** Permission needed to see it; null = any signed-in user of that area. */
    permission: z.string().nullable(),
    order: z.number().int(),
});
/** GET /api/v1/me/capabilities: everything the portal and apps need to build their UI. */
export const Capabilities = z.object({
    appVersion: z.string(),
    account: z.object({
        id: Uuid,
        email: z.string(),
        fullName: z.string(),
        locale: Locale,
        twoFactorEnabled: z.boolean(),
    }),
    /** Which area this session is in. 'building' (ADR 0034, C-SR1): a shared reception's building area. */
    area: z.enum(['tenant', 'platform', 'building']),
    /** Building area only (C-SR1): the building and the account's building role. Absent or null elsewhere. */
    building: z.object({ id: Uuid, name: z.string(), timeZone: TimeZone, role: z.enum(['building_admin', 'building_marshal']) }).nullable().optional(),
    /** Null in the platform area. */
    tenant: z
        .object({
        id: Uuid,
        name: z.string(),
        slug: Slug,
        /** Tenant default IANA time zone (tenant settings), else 'Europe/London'. */
        timeZone: TimeZone,
        branding: Branding,
        /** Branding logo as a short-lived signed URL, or null. */
        logoUrl: z.string().nullable(),
    })
        .nullable(),
    /** Null in the platform area. */
    user: z
        .object({
        id: Uuid,
        roleId: Uuid,
        roleName: z.string(),
        siteScope: SiteScope,
        siteIds: z.array(Uuid),
    })
        .nullable(),
    isPlatformAdmin: z.boolean(),
    /** Billing restriction of the tenant (ADR 0031), so the portal can show a banner. The server always sends it ('none' in the platform area); optional only so older mocks still type-check. */
    restriction: TenantRestriction.optional(),
    /** Other tenants this account can switch to (for the tenant switcher). */
    otherTenants: z.array(z.object({ tenantId: Uuid, name: z.string(), slug: Slug })),
    /** Effective permissions: tenant perms (filtered by enabled modules) or platform perms. */
    permissions: z.array(z.string()),
    modules: z.array(z.object({ key: z.string(), version: z.string(), enabled: z.boolean() })),
    /** Already filtered by area, enabled modules and permissions, sorted by order. */
    nav: z.array(NavEntry),
});
/** GET /api/v1/public/branding: login page branding, resolved from the Host header (or ?tenant=slug). */
export const PublicBranding = z.object({
    /** Null when the host is not a tenant address (default CloudSign branding). */
    tenantSlug: Slug.nullable(),
    displayName: z.string(),
    primaryColour: z.string(),
    accentColour: z.string(),
    logoUrl: z.string().nullable(),
});
//# sourceMappingURL=capabilities.js.map